import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiFilter, FiMapPin, FiBook, FiUsers, FiGrid, FiList } = FiIcons;

const TeachersDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Mock data for teachers with Jordanian locations
  const teachers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      school: 'Pine Valley Elementary',
      location: 'Amman, Jordan',
      subjects: ['Science', 'Math'],
      grades: ['3rd', '4th', '5th'],
      bio: 'Passionate about making STEM accessible to all students through hands-on learning.',
      photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300',
      activeProjects: 3,
      completedProjects: 12,
      totalRaised: 15420,
      joinDate: '2022'
    },
    {
      id: 2,
      name: 'Ahmed Al-Rashid',
      school: 'Al-Yarmouk Middle School',
      location: 'Irbid, Jordan',
      subjects: ['English', 'Literature'],
      grades: ['6th', '7th', '8th'],
      bio: 'Dedicated to fostering a love of reading and creative writing in young minds.',
      photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300',
      activeProjects: 2,
      completedProjects: 8,
      totalRaised: 9870,
      joinDate: '2023'
    },
    {
      id: 3,
      name: 'Fatima Al-Zahra',
      school: 'New Zarqa High School',
      location: 'Zarqa, Jordan',
      subjects: ['Art', 'Design'],
      grades: ['9th', '10th', '11th', '12th'],
      bio: 'Inspiring creativity and self-expression through visual arts and digital design.',
      photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300',
      activeProjects: 4,
      completedProjects: 15,
      totalRaised: 22340,
      joinDate: '2021'
    },
    {
      id: 4,
      name: 'Omar Khalil',
      school: 'Mafraq Community School',
      location: 'Mafraq, Jordan',
      subjects: ['Music', 'Drama'],
      grades: ['K', '1st', '2nd', '3rd'],
      bio: 'Bringing joy and confidence to young learners through music and performance.',
      photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300',
      activeProjects: 1,
      completedProjects: 6,
      totalRaised: 7250,
      joinDate: '2023'
    },
    {
      id: 5,
      name: 'Layla Mansour',
      school: 'Ajloun Heritage School',
      location: 'Ajloun, Jordan',
      subjects: ['History', 'Social Studies'],
      grades: ['9th', '10th', '11th', '12th'],
      bio: 'Making history come alive through interactive learning and real-world connections.',
      photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=300',
      activeProjects: 2,
      completedProjects: 10,
      totalRaised: 13560,
      joinDate: '2022'
    },
    {
      id: 6,
      name: 'Khalid Abu-Hassan',
      school: 'Jerash Middle School',
      location: 'Jerash, Jordan',
      subjects: ['Physical Education', 'Health'],
      grades: ['6th', '7th', '8th'],
      bio: 'Promoting healthy lifestyles and teamwork through sports and fitness activities.',
      photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300',
      activeProjects: 3,
      completedProjects: 9,
      totalRaised: 11200,
      joinDate: '2022'
    },
    {
      id: 7,
      name: 'Nour Al-Din',
      school: 'Madaba International School',
      location: 'Madaba, Jordan',
      subjects: ['Science', 'Chemistry'],
      grades: ['10th', '11th', '12th'],
      bio: 'Passionate about chemistry and inspiring students to explore the world of science.',
      photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300',
      activeProjects: 2,
      completedProjects: 7,
      totalRaised: 8900,
      joinDate: '2023'
    },
    {
      id: 8,
      name: 'Rana Qasemi',
      school: 'Salt Community College',
      location: 'Balqa, Jordan',
      subjects: ['Mathematics', 'Physics'],
      grades: ['11th', '12th'],
      bio: 'Making complex mathematical concepts accessible through innovative teaching methods.',
      photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300',
      activeProjects: 1,
      completedProjects: 5,
      totalRaised: 6750,
      joinDate: '2023'
    }
  ];

  const subjects = ['Science', 'Math', 'English', 'Literature', 'Art', 'Design', 'Music', 'Drama', 'History', 'Social Studies', 'Physical Education', 'Health', 'Chemistry', 'Physics', 'Mathematics'];
  const grades = ['K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];
  const locations = ['Amman', 'Irbid', 'Zarqa', 'Mafraq', 'Ajloun', 'Jerash', 'Madaba', 'Balqa', 'Karak', 'Tafileh', 'Maan', 'Aqaba'];

  // Filter teachers based on search and filters
  const filteredTeachers = useMemo(() => {
    return teachers.filter(teacher => {
      const matchesSearch = teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.school.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.bio.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSubject = !selectedSubject || teacher.subjects.includes(selectedSubject);
      const matchesGrade = !selectedGrade || teacher.grades.includes(selectedGrade);
      const matchesLocation = !selectedLocation || teacher.location.includes(selectedLocation);

      return matchesSearch && matchesSubject && matchesGrade && matchesLocation;
    });
  }, [searchTerm, selectedSubject, selectedGrade, selectedLocation]);

  const TeacherCard = ({ teacher, isListView = false }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
        isListView ? 'flex items-center space-x-6 p-6' : 'overflow-hidden'
      }`}
    >
      {isListView ? (
        <>
          <img
            src={teacher.photo}
            alt={teacher.name}
            className="w-20 h-20 rounded-full object-cover"
          />
          <div className="flex-1">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{teacher.name}</h3>
                <p className="text-gray-600">{teacher.school}</p>
                <p className="text-sm text-gray-500 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-1" />
                  {teacher.location}
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  {teacher.activeProjects} active • {teacher.completedProjects} completed
                </div>
                <div className="text-lg font-semibold text-primary-600">
                  ${teacher.totalRaised.toLocaleString()} raised
                </div>
              </div>
            </div>
            <p className="text-gray-700 mb-3">{teacher.bio}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {teacher.subjects.map((subject) => (
                <span
                  key={subject}
                  className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                >
                  {subject}
                </span>
              ))}
              {teacher.grades.map((grade) => (
                <span
                  key={grade}
                  className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                >
                  {grade}
                </span>
              ))}
            </div>
            <Link
              to={`/teacher/${teacher.id}`}
              className="text-primary-600 font-semibold hover:text-primary-700"
            >
              View Profile →
            </Link>
          </div>
        </>
      ) : (
        <>
          <img
            src={teacher.photo}
            alt={teacher.name}
            className="w-full h-48 object-cover"
          />
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-1">{teacher.name}</h3>
            <p className="text-gray-600 mb-1">{teacher.school}</p>
            <p className="text-sm text-gray-500 flex items-center mb-3">
              <SafeIcon icon={FiMapPin} className="mr-1" />
              {teacher.location}
            </p>
            <p className="text-gray-700 mb-4 line-clamp-2">{teacher.bio}</p>
            <div className="flex flex-wrap gap-1 mb-4">
              {teacher.subjects.slice(0, 2).map((subject) => (
                <span
                  key={subject}
                  className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                >
                  {subject}
                </span>
              ))}
              {teacher.subjects.length > 2 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{teacher.subjects.length - 2} more
                </span>
              )}
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
              <span>{teacher.activeProjects} active projects</span>
              <span>${teacher.totalRaised.toLocaleString()} raised</span>
            </div>
            <Link
              to={`/teacher/${teacher.id}`}
              className="block w-full text-center bg-primary-500 text-white py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              View Profile
            </Link>
          </div>
        </>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Teachers Directory</h1>
          <p className="text-gray-600">Discover passionate educators making a difference in classrooms across Jordan.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          {/* Search Bar */}
          <div className="relative mb-6">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search teachers, schools, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Grades</option>
              {grades.map((grade) => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>

            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>

            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 flex items-center justify-center py-2 px-4 rounded-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiGrid} className="mr-2" />
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 flex items-center justify-center py-2 px-4 rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiList} className="mr-2" />
                List
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center">
            <p className="text-gray-600">
              Showing {filteredTeachers.length} of {teachers.length} teachers
            </p>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedSubject('');
                setSelectedGrade('');
                setSelectedLocation('');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        </div>

        {/* Teachers Grid/List */}
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-6'}>
          {filteredTeachers.map((teacher) => (
            <TeacherCard key={teacher.id} teacher={teacher} isListView={viewMode === 'list'} />
          ))}
        </div>

        {filteredTeachers.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUsers} className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No teachers found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeachersDirectory;